import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useFormik } from 'formik';
import { useNavigate, useParams } from 'react-router-dom';
import { CompanyApi } from '../../../../../api/companyApi';
import {
  errorToast,
  successToast,
  getLocalStorageItem,
  minFutureDateString
} from '../../../../../utils/helper';
import Loader from '../../../../../component/common/Loader';
import Topbar from '../../../../PulseSurvey/Topbar';
import Breadcrumb from '../../../../../component/common/Breadcrumb';
import AddEditMilestoneValidation from '../../../../../validation/AddEditMilestoneValidation';
import MultiSelectField from '../components/MultiSelectField';
import ChecklistEditor from '../components/ChecklistEditor';
import ToggleSwitch from '../../../../../component/common/ToggleSwitch';
import { StandardButton, StandardInput } from '../../../../../component/common/StandardUI';

const toSelectOption = (item) =>
  typeof item === 'string' ? { _id: item, name: item } : { _id: item._id, name: item.name };

const AddEditMilestone = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const userData = JSON.parse(getLocalStorageItem('userData'));
  const slug = userData?.slug;
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [ownerOptions, setOwnerOptions] = useState([]);
  const [ownerLoading, setOwnerLoading] = useState(false);

  const formik = useFormik({
    initialValues: {
      title: '',
      endDate: '',
      assignedOwner: [],
      isAiSuggestion: false,
      checklist: []
    },
    validationSchema: AddEditMilestoneValidation,
    validateOnBlur: false,
    onSubmit: async (values) => {
      const payload = {
        title: values.title,
        endDate: values.endDate,
        assignedOwner: values.assignedOwner.map((item) => item._id),
        isAiSuggestion: values.isAiSuggestion,
        checklist: values.checklist
          .filter((item) => item.text.trim())
          .map((item) => ({ text: item.text.trim(), isCompleted: !!item.isCompleted }))
      };
      try {
        setLoading(true);
        const response = isEdit
          ? await CompanyApi.updateMilestone(id, payload)
          : await CompanyApi.createMilestone(payload);
        if (response?.data?.meta?.code === 1) {
          successToast(response?.data?.meta?.message || 'Milestone saved successfully');
          navigate(`/${slug}/people-plan/milestones`);
        } else {
          errorToast(response?.data?.meta?.message || 'Failed to save milestone');
        }
      } catch (error) {
        errorToast(error.message || 'Failed to save milestone');
      } finally {
        setLoading(false);
      }
    }
  });

  const searchOwners = async (searchKey) => {
    try {
      setOwnerLoading(true);
      const response = await CompanyApi.getPeoplePlanOwners(searchKey);
      if (response?.data?.meta?.code === 1) {
        setOwnerOptions(response?.data?.data || []);
      }
    } catch (error) {
      errorToast(error.message || 'Failed to search admins');
    } finally {
      setOwnerLoading(false);
    }
  };

  const fetchMilestone = async () => {
    try {
      setLoading(true);
      const response = await CompanyApi.getMilestoneById(id);
      if (response?.data?.meta?.code === 1) {
        const milestone = response?.data?.data;
        formik.setValues({
          title: milestone?.title || '',
          endDate: milestone?.endDate ? milestone.endDate.substring(0, 10) : '',
          assignedOwner: (milestone?.assignedOwner || []).map(toSelectOption),
          isAiSuggestion: !!milestone?.isAiSuggestion,
          checklist: milestone?.checklist || []
        });
      } else {
        errorToast(response?.data?.meta?.message || 'Failed to load milestone');
      }
    } catch (error) {
      errorToast(error.message || 'Failed to load milestone');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Admins and sub admins can both create; the backend limits a sub admin to
    // editing the records they created (an unauthorised edit comes back as "not found").
    if (isEdit) fetchMilestone();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const pages = [
    { name: 'People plan', href: `/${slug}/people-plan`, current: false },
    { name: 'Milestones', href: `/${slug}/people-plan/milestones`, current: false },
    { name: isEdit ? 'Edit Milestone' : 'New Milestone', href: '#', current: true }
  ];

  return (
    <div className="relative lg:mb-[45px]">
      {loading && <Loader />}
      <Helmet>
        <meta charSet="utf-8" />
        <title>{isEdit ? 'Edit Milestone' : 'New Milestone'} - People plan | Shoorah Admin</title>
      </Helmet>
      <Topbar title={isEdit ? 'Edit Milestone' : 'New Milestone'} infoBtn={false} />
      <Breadcrumb pageList={pages} className="lg:mt-8" />

      <div className="mt-6"></div>

      <form onSubmit={formik.handleSubmit} className="lg:mt-10 mt-6 space-y-6">
        <div className="rounded-input border-[1.5px] border-shoorah-borderGray bg-white dark:bg-shoorah-darkBgTabColor dark:border-shoorah-darkBorder sm:p-6 p-4 space-y-5">
          <div>
            <h2 className="text-lg font-medium text-gray-700 dark:text-gray-200">Add milestone</h2>
            <p className="text-sm text-gray-400 mt-1">
              Create a custom milestone that aligns with your company&apos;s people priorities.
            </p>
          </div>

          <StandardInput
            id="title"
            name="title"
            type="text"
            label="Milestone Title"
            placeholder="e.g. Launch Mental Health Ambassador Scheme"
            value={formik.values.title}
            onChange={formik.handleChange}
            error={formik.errors.title}
            isRequired
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <MultiSelectField
              label="Assign Owners"
              placeholder="Search users"
              options={ownerOptions}
              loading={ownerLoading}
              onSearch={searchOwners}
              selectedItems={formik.values.assignedOwner}
              onChange={(items) => formik.setFieldValue('assignedOwner', items)}
              error={formik.errors.assignedOwner}
              required
            />

            <StandardInput
              id="endDate"
              name="endDate"
              type="date"
              label="Deadline"
              min={minFutureDateString()}
              value={formik.values.endDate}
              onChange={formik.handleChange}
              error={formik.errors.endDate}
              isRequired
            />

            <div className="flex h-[40px] items-center justify-between gap-4 rounded-input border border-shoorah-borderGray dark:border-shoorah-darkBorder px-3">
              <p className="text-[14px] leading-5 font-medium text-gray-700 dark:text-gray-200">
                AI Suggestion
              </p>
              <ToggleSwitch
                toggleValue={formik.values.isAiSuggestion}
                setToggleValue={(value) => formik.setFieldValue('isAiSuggestion', value)}
                className="!bg-transparent !border-shoorah-btn dark:!border-shoorah-btnDark p-0.5"
                switchClassName="!h-4 !w-4 !bg-shoorah-btn dark:!bg-shoorah-btnDark"
              />
            </div>
          </div>
        </div>

        <div className="rounded-input border-[1.5px] border-shoorah-borderGray bg-white dark:bg-shoorah-darkBgTabColor dark:border-shoorah-darkBorder sm:p-6 p-4 space-y-5">
          <div>
            <h2 className="text-lg font-medium text-gray-700 dark:text-gray-200">Add checklist</h2>
            <p className="text-sm text-gray-400 mt-1">
              Create a task list to reach your milestone.
            </p>
          </div>

          <ChecklistEditor
            items={formik.values.checklist}
            onChange={(items) => formik.setFieldValue('checklist', items)}
          />
        </div>

        <div className="flex justify-end gap-3">
          <StandardButton text="Cancel" variant="secondary" onClick={() => navigate(-1)} />
          <StandardButton
            text={isEdit ? 'Save Changes' : 'Save Milestone'}
            type="submit"
            disabled={formik.isSubmitting}
          />
        </div>
      </form>
    </div>
  );
};

export default AddEditMilestone;
